import { cn } from "@/lib/utils";
import { Integration } from "@cohesive/api-client";
import { motion } from "framer-motion";
import { Building2, ChevronRight, Settings } from "lucide-react";
import { useMemo } from "react";
import { IntegrationIcon } from "../IntegrationIcon";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader } from "../ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

// Platform definitions with colors matching the task card design
const PLATFORMS = {
  salesforce: {
    name: "Salesforce",
    color: "from-rust to-rust",
    bgColor: "bg-spectrum-blue-light/15 dark:bg-spectrum-blue-dark/20",
  },
  hubspot: {
    name: "HubSpot",
    color: "from-rust to-rust",
    bgColor: "bg-lc-warning/10 dark:bg-orange-950/20",
  },
  slack: {
    name: "Slack",
    color: "from-plum to-purple-600",
    bgColor: "bg-purple-50 dark:bg-plum/20",
  },
  "google-mail": {
    name: "Gmail",
    color: "from-lc-blocked to-spectrum-red",
    bgColor: "bg-lc-blocked/10 dark:bg-spectrum-red-dark/20",
  },
  shopify: {
    name: "Shopify",
    color: "from-lc-active to-spectrum-green",
    bgColor: "bg-lc-active/10 dark:bg-spectrum-green-dark/20",
  },
  "google-drive": {
    name: "Google Drive",
    color: "from-gold to-spectrum-yellow",
    bgColor: "bg-gold/10 dark:bg-gold/10",
  },
  pipedream: {
    name: "Pipedream",
    color: "from-spectrum-indigo to-spectrum-indigo-dark",
    bgColor: "bg-spectrum-indigo-light/15 dark:bg-spectrum-indigo-dark/20",
  },
  nango: {
    name: "Nango",
    color: "from-teal-500 to-teal-600",
    bgColor: "bg-teal-50 dark:bg-teal-950/20",
  },
} as const;

// Helper function to get category display name
function getCategoryDisplayName(category: string): string {
  return category
    .split(/[-_]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export type ConnectionActionType = "connect" | "contact-admin" | "discoverable";

interface ConnectionActionButtonProps {
  actionType: ConnectionActionType;
  onConnect?: () => void;
  onRequestAccess?: () => void;
}

const ConnectionActionButton = ({
  actionType,
  onConnect,
  onRequestAccess,
}: ConnectionActionButtonProps) => {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (actionType === "connect") {
      onConnect?.();
    } else if (actionType === "discoverable") {
      onRequestAccess?.();
    }
    // contact-admin case doesn't have a handler since it's disabled
  };

  if (actionType === "contact-admin") {
    return (
      <div className="flex items-center gap-1">
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            disabled
            className="h-7 px-2 text-xs flex items-center gap-1 hover:text-foreground transition-colors"
          >
            <span className="text-xs font-medium">
              Contact Admin to Connect
            </span>
          </Button>
        </div>
      </div>
    );
  }

  if (actionType === "discoverable") {
    return (
      <div className="flex items-center gap-1">
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs flex items-center gap-1 text-muted-foreground"
            onClick={handleClick}
          >
            <span className="text-xs font-medium">Request access</span>
            <ChevronRight className="h-3 w-3" />
          </Button>
        </div>
      </div>
    );
  }

  // Default connect case
  return (
    <div className="flex items-center gap-1">
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-xs flex items-center gap-1 text-muted-foreground"
          onClick={handleClick}
        >
          <span className="text-xs font-medium">Connect</span>
          <ChevronRight className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
};

export interface ConnectionCardProps {
  integration: Integration;
  index?: number;
  isConnected?: boolean;
  onClick?: React.MouseEventHandler<HTMLDivElement> | undefined;
  onConnect?: () => void;
  onManage?: () => void;
  onRequestAccess?: () => void;
  showCategory?: boolean;
  actionType?: ConnectionActionType;
}

export const ConnectionCard = ({
  integration,
  index = 0,
  isConnected,
  onClick,
  onConnect,
  onManage,
  onRequestAccess,
  showCategory = true,
  actionType = "connect",
}: ConnectionCardProps) => {
  const platform = PLATFORMS[integration.platform as keyof typeof PLATFORMS];

  const category = useMemo(() => {
    return integration.metadata?.categories?.[0]
      ? getCategoryDisplayName(integration.metadata.categories[0])
      : null;
  }, [integration]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3, delay: index * 0 }}
      onClick={onClick}
      className={cn("relative group", onClick && "cursor-pointer")}
    >
      <Card
        className={cn(
          "relative flex flex-col overflow-hidden hover:bg-neutral-50 dark:hover:bg-neutral-200 shadow-sm hover:shadow-md transition-all duration-300 h-full",
        )}
      >
        <CardHeader
          className={cn(
            "flex-1 relative",
            isConnected ? "pb-1 pt-3 px-3" : "pb-2",
          )}
        >
          {/* Manage button for connected integrations - only show on hover */}
          {isConnected && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                      onClick={(e) => {
                        e.stopPropagation();
                        onManage?.();
                      }}
                    >
                      <Settings className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Manage</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          )}

          {/* Icon, Title, and Category layout */}
          <div
            className={cn(
              "flex items-center gap-3",
              isConnected ? "pb-2" : "pb-4",
            )}
          >
            {/* Icon container */}
            <div
              className={cn(
                "rounded-lg flex items-center justify-center flex-shrink-0",
                isConnected ? "w-10 h-10" : "w-12 h-12",
                platform?.bgColor || "bg-neutral-50 dark:bg-neutral-800",
              )}
            >
              <IntegrationIcon
                platform={integration.platform}
                name={integration.name}
                imageURL={integration.metadata?.image_url}
                className={isConnected ? "w-6 h-6" : "w-8 h-8"}
              />
            </div>

            {/* Title and Category column */}
            <div className="flex-1 min-w-0">
              {/* Integration title with organization icon */}
              <div className="flex items-center gap-2 mb-1">
                <h3
                  className={cn(
                    "font-medium leading-tight text-foreground line-clamp-2",
                    isConnected ? "text-sm" : "text-base",
                  )}
                >
                  {integration.name}
                </h3>
                {integration.auth_level === "organization" && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Building2 className="w-4 h-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>This is an organization-wide connection</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>

              {/* Category information */}
              {showCategory && category && !isConnected && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="font-medium">{category}</span>
                </div>
              )}
            </div>
          </div>

          {/* Description - only show for non-connected integrations */}
          {!isConnected && integration.description && (
            <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed mb-3">
              {integration.description}
            </p>
          )}
        </CardHeader>

        {/* Only show CardContent for non-connected integrations */}
        {!isConnected && (
          <CardContent className="pt-0 pb-4 pr-3 mt-auto flex flex-col gap-2">
            <div className="flex items-center justify-end">
              <ConnectionActionButton
                actionType={actionType}
                onConnect={onConnect}
                onRequestAccess={onRequestAccess}
              />
            </div>
          </CardContent>
        )}
      </Card>
    </motion.div>
  );
};
